import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'

// Layout
import Layout from './components/layout/Layout'
import MaintenancePage from './pages/MaintenancePage'
import { useUser } from './contexts/UserContext'

const IS_MAINTENANCE = import.meta.env.VITE_MAINTENANCE_MODE === 'true'
const MAINTENANCE_WHITELIST_USERNAMES = [
  'danil123',
  'elfyonok',
  'elbek45',
  'elbek',
  'elbekevony',
  'ndoubleu',
  'test45',
  'test123',
  'genesis',
]

// Pages
import LandingPage from './pages/LandingPage'
import VaultsPage from './pages/VaultsPage'
import VaultDetailPage from './pages/VaultDetailPage'
import DashboardPage from './pages/DashboardPage'
import PortfolioPage from './pages/PortfolioPage'
import MarketplacePage from './pages/MarketplacePage'
import ProfilePage from './pages/ProfilePage'
import FAQPage from './pages/FAQPage'
import LegalPage from './pages/LegalPage'

// Admin Pages - lazy loaded
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'))
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'))
const AdminVaultsPage = lazy(() => import('./pages/admin/AdminVaultsPage'))
const AdminBoostTokensPage = lazy(() => import('./pages/admin/AdminBoostTokensPage'))
const AdminTreasuryPage = lazy(() => import('./pages/admin/AdminTreasuryPage'))
const AdminTakaraStatsPage = lazy(() => import('./pages/admin/AdminTakaraStatsPage'))
const AdminPartnersPage = lazy(() => import('./pages/admin/AdminPartnersPage'))
const AdminClaimsPage = lazy(() => import('./pages/admin/AdminClaimsPage'))
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'))
const AdminReferrersPage = lazy(() => import('./pages/admin/AdminReferrersPage'))
const AdminMiningVaultsPage = lazy(() => import('./pages/admin/AdminMiningVaultsPage'))
const AdminInstantSalesPage = lazy(() => import('./pages/admin/AdminInstantSalesPage'))
const AdminReportsPage = lazy(() => import('./pages/admin/AdminReportsPage'))
const AdminTiersPage = lazy(() => import('./pages/admin/AdminTiersPage'))
const AdminAuditPage = lazy(() => import('./pages/admin/AdminAuditPage'))
const AdminContractPage = lazy(() => import('./pages/admin/AdminContractPage'))

// Referrer Portal Pages - lazy loaded
const ReferrerLoginPage = lazy(() => import('./pages/referrer/ReferrerLoginPage'))
const ReferrerDashboardPage = lazy(() => import('./pages/referrer/ReferrerDashboardPage'))

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
      <p className="text-neutral-400">Loading...</p>
    </div>
  </div>
)

// Redirect / to /app (preserving query params like ?ref=)
function RootRedirect() {
  const location = useLocation()
  return <Navigate to={`/app${location.search}`} replace />
}

// Redirect legacy vault detail URLs to /app/vaults/:id
function VaultRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/app/vaults/${id}`} replace />
}

function App() {
  const { user, isAuthenticated, loading, tokenValidated } = useUser()

  // Check if user is whitelisted for maintenance bypass
  const isWhitelisted = isAuthenticated && user?.username &&
    MAINTENANCE_WHITELIST_USERNAMES.includes(user.username.toLowerCase())

  // During maintenance: show app normally if whitelisted, show maintenance page if authenticated but not whitelisted
  const showMaintenanceBlock = IS_MAINTENANCE && !isWhitelisted

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Admin Routes — always accessible, even during maintenance */}
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/claims" element={<AdminClaimsPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/tiers" element={<AdminTiersPage />} />
          <Route path="/admin/vaults" element={<AdminVaultsPage />} />
          <Route path="/admin/mining-vaults" element={<AdminMiningVaultsPage />} />
          <Route path="/admin/takara-stats" element={<AdminTakaraStatsPage />} />
          <Route path="/admin/boost-tokens" element={<AdminBoostTokensPage />} />
          <Route path="/admin/treasury" element={<AdminTreasuryPage />} />
          <Route path="/admin/partners" element={<AdminPartnersPage />} />
          <Route path="/admin/referrers" element={<AdminReferrersPage />} />
          <Route path="/admin/instant-sales" element={<AdminInstantSalesPage />} />
          <Route path="/admin/reports" element={<AdminReportsPage />} />
          <Route path="/admin/audit" element={<AdminAuditPage />} />
          <Route path="/admin/contract" element={<AdminContractPage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />

          {/* Referrer Portal — always accessible */}
          <Route path="/referrer" element={<Navigate to="/referrer/login" replace />} />
          <Route path="/referrer/login" element={<ReferrerLoginPage />} />
          <Route path="/referrer/dashboard" element={<ReferrerDashboardPage />} />

          {/* Root redirects to /app (preserving referral codes) */}
          <Route path="/" element={<RootRedirect />} />

          {/* Main App Routes — Landing page always accessible for login/register */}
          <Route path="/app" element={<Layout />}>
            <Route index element={<LandingPage />} />
            {showMaintenanceBlock ? (
              <>
                {/* Authenticated but not whitelisted — maintenance on all inner pages */}
                <Route path="*" element={<MaintenancePage />} />
              </>
            ) : (
              <>
                <Route path="vaults" element={<VaultsPage />} />
                <Route path="vaults/:id" element={<VaultDetailPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="portfolio" element={<PortfolioPage />} />
                <Route path="marketplace" element={<MarketplacePage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="faq" element={<FAQPage />} />
                <Route path="legal" element={<LegalPage />} />
              </>
            )}
          </Route>

          {/* Legacy routes redirect to /app */}
          <Route path="/vaults" element={<Navigate to="/app/vaults" replace />} />
          <Route path="/vaults/:id" element={<VaultRedirect />} />
          <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/portfolio" element={<Navigate to="/app/portfolio" replace />} />
          <Route path="/marketplace" element={<Navigate to="/app/marketplace" replace />} />
          <Route path="/faq" element={<Navigate to="/app/faq" replace />} />

          {/* Catch-all: redirect to /app */}
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </Suspense>

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1a2420',
            color: '#fafafa',
            border: '1px solid #064e3b',
          },
        }}
      />
    </>
  )
}

export default App
